<template>
	<div class="container-fluid">
	    <div class="jumbotron py-4 mb-5">
	        <h1>Documentation</h1>
		    <div class="mt-5" id="table-contents">
		        <h3>Table of contents</h3>
		        <ul>
		            <li class="doc"><a href="#hmm-anchor">HMM Logo</a></li>
		                <ul>
		                    <li><a href="#dom-logo-anchor">Domain logo</a></li>
		                    <li><a href="#col-info-anchor">Column informations</a></li>
		                    <li><a href="#mut-tab-anchor">Mutation Table</a></li>
		                    <li><a href="#your-logo-anchor">Create your HMM logo (Skylign)</a></li>
		                </ul>
		            <li class="doc"><a href="#alignment-anchor">Alignment Viewer</a></li>
		                <ul>
		                    <li><a href="#logo-pos-anchor">Logo position number</a></li>
		                    <li><a href="#color-anchor">Colors</a></li>
		                    <li><a href="#get-ali-anchor">Get protein domains alignments (Pfam)</a></li>
		                </ul>
		            <li class="doc"><a href="#freq-anchor">Frequency Viewer</a></li>
		                <ul>
		                    <li><a href="#dom-feat-anchor">Domain feature</a></li>
		                    <li><a href="#freq-feat-anchor">Frequencies features</a></li>
		                    <li><a href="#variants-freq-anchor">Variants frequency (GnomAD)</a></li>
		                </ul>
		        </ul>
		    </div>
	    </div>

	    <b-card
	    class="my-4"
	    style="font-size: 16px;"
	    >
	    	<a id="hmm-anchor"><h2 class="text-center">HMM Logo</h2></a>
	    	<hr class="my-2">
	    	<h4 class="mt-3" id="dom-logo-anchor"><i class="fas fa-chevron-right mr-2"></i>Domain logo</h4>
	    	<b-media no-body class="mt-4">
	    		<b-media-aside vertical-align="center">
	    			<b-img src="../images/doc/doc_logo_letter.png" height="150" alt="doc_logo_letter"></b-img>
	    		</b-media-aside>
				<b-media-body class="text-justify">
					<p>
				        A HMM Logo displays several columns containing a letters stack, representing the different amino acids observed at this position. The height stack shows the <b>position conservation</b> while the letter height represents the <b>amino acid frequency</b> at this specific position.
				    </p>
				    <p>
				        The three lines underlying the columns letter stack displays -from the top to the bottom- respectivly: the <b>probability to observe an amino acid</b>, the <b>insertion probability</b> and the <b>insertion length</b> at this logo position.
				    </p>  
				</b-media-body>
	    	</b-media>
	    	<b-media no-body>
	    		<b-media-aside vertical-align="center">
	    			<b-img src="../images/doc/doc_lines_probs.png" height="50" alt="doc_lines_probs"></b-img>
	    		</b-media-aside>
	    		<b-media-body class="text-justify">
	    			<p>
				        In simple terms, the last two lines represent the probability to observe a letter insertion right after this column and the expected length of this insertion.
				        <br>
				        In our case the first line represents how many eukaryotes sequences in the pfam domain alignment have an amino acid at this location.
				        The two last lines have low insertion probability and low expected length because we chose to represent all the alignment position to make easier the <b>human proteins mapping</b>.
				    </p>
				    <p>
				    	Actually, if you need a consensus protein domain HMM logo you can go to the <a href="http://pfam.xfam.org/" target="_blank">Pfam website</a>. Only more conservated position are represented on this logo. Nevertheless, it is harder to map human protein positions on this type of display. That's why Pfam logo is more helpfull determining most important amino acid patterns in the protein domain.
				    </p>
	    		</b-media-body>
	    	</b-media>

	    	<hr class="my-2">
	    	<h4 class="mt-3" id="col-info-anchor"><i class="fas fa-chevron-right mr-2"></i>Column information</h4>
	    	<b-media no-body class="mt-4">
	    		<b-media-aside>
	    			<b-img class="mb-4" src="../images/doc/doc_col_info.png" width="300" alt="doc_col_info"></b-img>
	    		</b-media-aside>
	    		<b-media-body class="my-auto">
	    			<p>
				        By clicking on a logo column you can display a table including more details on amino acids <b>frequencies</b>. Amino acids are ordered by <b>decreasing frequency</b>.
				        The explainations of the table two first lines are in the previous paragraph.
				    </p>
	    		</b-media-body>
	    	</b-media>

	    	<hr class="my-2">
	    	<h4 class="mt-3" id="mut-tab-anchor"><i class="fas fa-chevron-right mr-2"></i>Mutation table</h4>
	    	<b-media class="mt-4 text-justify">
	    			<b-row align-h="center"><b-img style="max-width: 95%;" class="mb-2" src="../images/doc/doc_table.png" alt="doc_table"></b-img></b-row>
	    			<p>
				        The mutation table shows the Dolphin prediction for the given protein missense mutation. Dolphin "WT" and "∆" scores are displayed and the prediction probability (see Dolphin paper for more information). The red button downloads the mutation information.<br>
				        GnomAD allele frequency and Dolphin frequency (AF in the domain) are also available.
				    </p>
				    <p>To understand where the Dolphin frequency comes from, you can click on the "Show Details" button.
				    </p>
				    <b-row align-h="center"><b-img style="max-width: 95%;" class="mb-2" src="../images/doc/doc_details_mut_table.png" height="350" alt="doc_details_mut_table"></b-img></b-row>
	    			<p>
				        The details table displays the same missenses variants at the same logo position. Mutations are ordered by decreased frequencies. The first one gives the Dolphin  frequency.
				        For each mutant you can also see the reported gnomAD frequency.
				    </p>
	    	</b-media>

	    	<hr class="my-2">
	    	<h4 class="mt-3" id="your-logo-anchor"><i class="fas fa-chevron-right mr-2"></i>Create your HMM logo</h4>
	    	<b-media class="mt-4 text-justify">
	    		<p> 
	    			All the protein domains HMM Logos were created by the <b>Skylign</b> tool. To create your logo you can go to their website and read the relative documentation. Differents possibilities are offered to generate a logo that corresponds to your alignment data.
	    		</p>
	    		<h5>Skylign web site:</h5>
		    	<p class="pl-2"><a href="http://skylign.org/" target="_blank">skylign.org<img width="20px" class="ml-3 logo-size-max" alt="Logo Skylign" src="http://skylign.org/static/gfx/favicon.ico"></a></p>
		    	<h5>Citation in publications:</h5>
		    	<p class="pl-2">Wheeler, T.J., Clements, J. & Finn, R.D. Skylign: a tool for creating informative, interactive logos representing sequence alignments and profile hidden Markov models. BMC Bioinformatics 15, 7 (2014). <a style="color: #2196F3;" href="https://bmcbioinformatics.biomedcentral.com/articles/10.1186/1471-2105-15-7#citeas" target="_blank">https://doi.org/10.1186/1471-2105-15-7</a></p>
	    	</b-media>
	    </b-card>

	    <b-card
	    class="my-4"
	    style="font-size: 16px;"
	    >
	    	<h2 class="text-center" id="alignment-anchor">Alignment Viewer</h2>
	    	<hr class="my-2">
	    	<h4 class="mt-3" id="logo-pos-anchor"><i class="fas fa-chevron-right mr-2"></i>Logo position number</h4>
	    	<b-media class="mt-4 text-justify">
	    		<b-row align-h="center"><b-img style="max-width: 95%;" class="mb-2" src="../images/doc/doc_align.png" height="300" alt="doc_align"></b-img></b-row>
	    		<p>
	    			The alignment viewer displays all the <b>human proteins</b> belonging to this pfam domain alignment.
	    			<br>
	    			The first line represents the HMM logo positions (columns). In the example above, the provided variant is at the center of the highlighted blue cross. The <b>position in the logo</b> corresponds to the <b>vertical highlight</b> while the <b>horizontal</b> one corresponds to the <b>specific domain</b> of the human protein.
	    			<br>
	    			By hovering over the entry name the <b>gene name</b> will appear. 
	    		</p>
	    	</b-media>

	    	<hr class="my-2">
	    	<h4 class="mt-3" id="color-anchor"><i class="fas fa-chevron-right mr-2"></i>Colors</h4>
	    	<b-media no-body class="mt-4">
	    		<b-media-aside vertical-align="center">
	    			<b-img class="mb-4" src="../images/doc/doc_color_2.png" height="140" alt="doc_col_info"></b-img>
	    		</b-media-aside>
	    		<b-media-body class="my-auto">
	    			<p>
				        This select form proposes several colors panels: 
				      </p>
				        <ul>
				        	<li>Skylign<i v-b-toggle.collapse-1 @click="buttons[0] = !buttons[0]" class="fas ml-2" v-bind:class="[buttons[0] ? 'fa-caret-down' : 'fa-caret-up']"></i></li>
					        	<b-collapse id="collapse-1" class="mt-2">
					        		<b-table outlined style="width: 650px;" small :items="skylign" :fields="fields1" thead-class="d-none" >
					        			<template #cell(col1)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col1.col)" v-b-tooltip.hover :title="data.item.col1.col + ' click to copy'" :style="'width: 20px;display: inline-block;text-align: center;vertical-align: middle;height:22px;width:20px;margin-right:10px;background:' + data.item.col1.col + ';'"></span>
					        				<span style="width: 20px;display: inline-block;text-align: center;vertical-align: middle;">{{data.item.col1.AA}}</span>
					        			</template>
					        			<template #cell(col2)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col2.col)" v-b-tooltip.hover :title="data.item.col2.col + ' click to copy'" :style="'width: 20px;display: inline-block;text-align: center;vertical-align: middle;height:22px;width:20px;margin-right:10px;background:' + data.item.col2.col + ';'"></span>
					        				<span style="width: 20px;display: inline-block;text-align: center;vertical-align: middle;">{{data.item.col2.AA}}</span>
					        			</template>
					        			<template #cell(col3)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col3.col)" v-b-tooltip.hover :title="data.item.col3.col + ' click to copy'" :style="'width: 20px;display: inline-block;text-align: center;vertical-align: middle;height:22px;width:20px;margin-right:10px;background:' + data.item.col3.col + ';'"></span>
					        				<span style="width: 20px;display: inline-block;text-align: center;vertical-align: middle;">{{data.item.col3.AA}}</span>
					        			</template>
					        			<template #cell(col4)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col4.col)" v-b-tooltip.hover :title="data.item.col4.col + ' click to copy'" :style="'width: 20px;display: inline-block;text-align: center;vertical-align: middle;height:22px;width:20px;margin-right:10px;background:' + data.item.col4.col + ';'"></span>
					        				<span style="width: 20px;display: inline-block;text-align: center;vertical-align: middle;">{{data.item.col4.AA}}</span>
					        			</template>
					        		</b-table>
					        	</b-collapse>
				        	<li>Rasmol<i v-b-toggle.collapse-2 class="fas fa-caret-down ml-2"></i></li>
				        		<b-collapse id="collapse-2" class="mt-2">
					        		<b-table outlined style="width: 250px; text-align: center;" small :items="rasmol" :fields="fields2" thead-class="d-none" >
					        			<template #cell(colo)="data">
					        				<span @click="copy_data_to_clipboard(data.item.colo)" v-b-tooltip.hover :title="data.item.colo + ' click to copy'" :style="'display: inline-block;width: 20px;text-align: center;vertical-align: middle;height:22px;width:20px;background:' + data.item.colo + ';'"></span>
					        			</template>
					        		</b-table>
					        	</b-collapse>
				        	<li>Rasmol Shapely<i v-b-toggle.collapse-3 class="fas fa-caret-down ml-2"></i></b-button></li>
				        		<b-collapse id="collapse-3" class="mt-2">
					        		<b-table outlined style="width: 650px;" small :items="rasmolshapely" :fields="fields1" thead-class="d-none" >
					        			<template #cell(col1)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col1.col)" v-b-tooltip.hover :title="data.item.col1.col + ' click to copy'" :style="'width: 20px;display: inline-block;text-align: center;vertical-align: middle;height:22px;width:20px;margin-right:10px;background:' + data.item.col1.col + ';'"></span>
					        				<span style="width: 20px;display: inline-block;text-align: center;vertical-align: middle;">{{data.item.col1.AA}}</span>
					        			</template>
					        			<template #cell(col2)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col2.col)" v-b-tooltip.hover :title="data.item.col2.col + ' click to copy'" :style="'width: 20px;display: inline-block;text-align: center;vertical-align: middle;height:22px;width:20px;margin-right:10px;background:' + data.item.col2.col + ';'"></span>
					        				<span style="width: 20px;display: inline-block;text-align: center;vertical-align: middle;">{{data.item.col2.AA}}</span>
					        			</template>
					        			<template #cell(col3)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col3.col)" v-b-tooltip.hover :title="data.item.col3.col + ' click to copy'" :style="'width: 20px;display: inline-block;text-align: center;vertical-align: middle;height:22px;width:20px;margin-right:10px;background:' + data.item.col3.col + ';'"></span>
					        				<span style="width: 20px;display: inline-block;text-align: center;vertical-align: middle;">{{data.item.col3.AA}}</span>
					        			</template>
					        			<template #cell(col4)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col4.col)" v-b-tooltip.hover :title="data.item.col4.col + ' click to copy'" :style="'width: 20px;display: inline-block;text-align: center;vertical-align: middle;height:22px;width:20px;margin-right:10px;background:' + data.item.col4.col + ';'"></span>
					        				<span style="width: 20px;display: inline-block;text-align: center;vertical-align: middle;">{{data.item.col4.AA}}</span>
					        			</template>
					        		</b-table>
					        	</b-collapse>
				        	<li>ClustalX<i v-b-toggle.collapse-4 class="fas fa-caret-down ml-2"></i></b-button></li>
				        		<b-collapse id="collapse-4" class="mt-2">
					        		<b-table outlined style="width: 400px; text-align: center;" small :items="clustalx" :fields="fields3" thead-class="d-none" >
					        			<template #cell(col)="data">
					        				<span @click="copy_data_to_clipboard(data.item.col)" v-b-tooltip.hover :title="data.item.col + ' click to copy'" :style="'display: inline-block;width: 20px;text-align: center;vertical-align: middle;height:22px;width:20px;background:' + data.item.col + ';'"></span>
					        			</template>
					        		</b-table>
					        	</b-collapse>
				        </ul>
	    		</b-media-body>
	    	</b-media>

	    	<hr class="my-2">
	    	<h4 class="mt-3" id="get-ali-anchor"><i class="fas fa-chevron-right mr-2"></i>Get protein domains alignments</h4>
	    	<b-media class="mt-4 text-justify">
	    		<p> 
	    			Protein domains sequences alignments were obtained from the <b>Pfam FTP</b>. Only <b>Pfam A</b> (an HMM based hand curated Pfam entry, which passed a manually set threshold value for each HMM) is used, keeping only the eukaryotes sequences. In the Dolphin alignment viewer, you can see sequences from human proteins.
	    		</p>
	    		<h5>Pfam web site:</h5>
		        <p class="pl-2"><a href="http://pfam.xfam.org/" target="_blank">pfam.xfam.org<img width="50px" class="ml-3 logo-size-max" alt="Logo Pfam" src="/images/logo_pfam.png"></a></p>
		        <h5>Citation in publications:</h5>
		        <p class="pl-2">Jaina Mistry, Sara Chuguransky, Lowri Williams, Matloob Qureshi, Gustavo A Salazar, Erik L L Sonnhammer, Silvio C E Tosatto, Lisanna Paladin, Shriya Raj, Lorna J Richardson, Robert D Finn, Alex Bateman, Pfam: The protein families database in 2021, Nucleic Acids Research, Volume 49, Issue D1, 8 January 2021, Pages D412–D419, <a href="https://academic.oup.com/nar/article/49/D1/D412/5943818" style="color: #2196F3;" target="_blank">https://doi.org/10.1093/nar/gkaa913</a></p>
	    	</b-media>
	    </b-card>

	    <b-card
	    class="my-4"
	    style="font-size: 16px;"
	    >
	    	<h2 class="text-center" id="freq-anchor">Frequency viewer</h2>
	    	<hr class="my-2">
	    	<h4 class="mt-3" id="dom-feat-anchor"><i class="fas fa-chevron-right mr-2"></i>Domain feature</h4>
	    	<b-media no-body class="mt-4">
	    		<b-media-aside vertical-align="center">
	    			<b-img class="mb-4" src="../images/doc/doc_feature.png" width="380" alt="doc_feature"></b-img>
	    		</b-media-aside>
	    		<b-media-body class="my-auto text-justify">
	    			<p>
	    				The first line of the frequency viewer element, displays the <b>protein amino acid sequence</b> associated with the Pfam entry name related to the provided mutation. Letter color depends on the alignement viewer color selector (see paragraph above).
	    				<br>
	    				The second line represents differents occurancies of the same protein domain along the protein. At the bottom, the dark line shows the protein scale (protein position).
	    			</p>
	    		</b-media-body>
	    	</b-media>

	    	<hr class="my-2">
	    	<h4 class="mt-3" id="freq-feat-anchor"><i class="fas fa-chevron-right mr-2"></i>Frequencies features</h4>
	    	<b-media no-body class="mt-4">
	    		<b-media-aside vertical-align="center">
	    			<b-img class="mb-4" src="../images/doc/doc_zoom_feature.png" width="380" alt="doc_zoom_feature"></b-img>
	    		</b-media-aside>
	    		<b-media-body class="my-auto text-justify">
	    			<p>
	    				With your mouse, you can scroll or click and drag to zoom in the frequency viewer. To unzoom, you can scroll or right click in the viewer.
	    				<br>
	    				Both stair lines display the <b>number of amino acid substitutions</b> with associated with a GnomAD (yellow, top) or Dolphin (blue, bottom) frequency.
	    			</p>
	    		</b-media-body>
	    	</b-media>

	    	<hr class="my-2">
	    	<h4 class="mt-3" id="variants-freq-anchor"><i class="fas fa-chevron-right mr-2"></i>Variants frequency</h4>
	    	<b-media class="mt-4 text-justify">
	    		<p> 
	    			All substitution frequencies were extracted from<b> GnomAD version 2</b> containing data from 125,748 exomes. For each missense mutation, we selected the <b>most frequent mutational event</b> leading to the amino acid substitution from any human population.
	    		</p>
	    		<h5>GnomAD web site:</h5>
		        <p class="pl-2"><a href="https://gnomad.broadinstitute.org/" target="_blank">gnomad.broadinstitute.org<img width="60px" class="ml-3 logo-size-max" alt="Logo GnomAD" src="/images/logo_gnomad.png"></a></p>
		        <h5>Citation in publications:</h5>
		        <p class="pl-2">Karczewski, K.J., Francioli, L.C., Tiao, G. et al. The mutational constraint spectrum quantified from variation in 141,456 humans. Nature 581, 434–443 (2020). <a href="https://www.nature.com/articles/s41586-020-2308-7#citeas" style="color: #2196F3;" target="_blank">https://doi.org/10.1038/s41586-020-2308-7</a></p>
	    	</b-media>
	    </b-card>

	</div>
</template>



<script>
export default {
	data(){
		return {
			buttons: [true,true,true,true],
			fields1: [{key:'col1'},{key:'col2'},{key:'col3'},{key:'col4'}],
			fields2: [{key:'AA'},{key:'colo'}],
			fields3: [{key:'category'},{key:'AA'},{key:'col'}],
			skylign: [
				{col1: {AA:'A',col:'#FF9966'}, col2: {AA:'G',col:'#f2f20c'}, col3: {AA:'M',col:'#CC99CC'}, col4: {AA:'S',col:'#0000FF'}},
				{col1: {AA:'C',col:'#009999'}, col2: {AA:'H',col:'#660033'}, col3: {AA:'N',col:'#336666'}, col4: {AA:'T',col:'#00FFFF'}},
				{col1: {AA:'D',col:'#FF0000'}, col2: {AA:'I',col:'#CC9933'}, col3: {AA:'P',col:'#0099FF'}, col4: {AA:'V',col:'#FFCC33'}},
				{col1: {AA:'E',col:'#CC0033'}, col2: {AA:'K',col:'#663300'}, col3: {AA:'Q',col:'#6666CC'}, col4: {AA:'W',col:'#66CC66'}},
				{col1: {AA:'F',col:'#00FF00'}, col2: {AA:'L',col:'#FF9933'}, col3: {AA:'R',col:'#990000'}, col4: {AA:'Y',col:'#006600'}}
			],
			rasmol: [
				{AA:'A', colo:'#C8C8C8'},{AA:'R / K', colo:'#145AFF'},{AA:'N / Q', colo:'#00DCDC'},{AA:'D / E', colo:'#E60A0A'},{AA:'C / M', colo:'#E6E600'},{AA:'G', colo:'#ccc'},{AA:'H', colo:'#8282D2'},{AA:'I / L / V', colo:'#0F820F'},{AA:'F / Y', colo:'#3232AA'},{AA:'P', colo:'#DC9682'},{AA:'S / T', colo:'#FA9600'},{AA:'W', colo:'#B45AB4'}
			],
			rasmolshapely: [
				{col1: {AA:'A',col:'#8CFF8C'}, col2: {AA:'G',col:'#ccc'}, col3: {AA:'M',col:'#B8A042'}, col4: {AA:'S',col:'#FF7042'}},
				{col1: {AA:'C',col:'#FFFF70'}, col2: {AA:'H',col:'#7070FF'}, col3: {AA:'N',col:'#FF7C70'}, col4: {AA:'T',col:'#B84C00'}},
				{col1: {AA:'D',col:'#A00042'}, col2: {AA:'I',col:'#004C00'}, col3: {AA:'P',col:'#525252'}, col4: {AA:'V',col:'#FF8CFF'}},
				{col1: {AA:'E',col:'#660000'}, col2: {AA:'K',col:'#4747B8'}, col3: {AA:'Q',col:'#FF4C4C'}, col4: {AA:'W',col:'#4F4600'}},
				{col1: {AA:'F',col:'#534C42'}, col2: {AA:'L',col:'#455E45'}, col3: {AA:'R',col:'#00007C'}, col4: {AA:'Y',col:'#8C704C'}}
			],
			clustalx: [
				{category:'Hydrophobics',AA:'A / I / L / M / F / W / V',col:'#80A0F0'},
				{category:'Charged (+ / -) ',AA:'K / R / E / D',col:'#C048BF'},
				{category:'Polars',AA:'N / Q / S / T',col:'#13C014'},
				{category:'Cysteines',AA:'C',col:'#F08080'},
				{category:'Glycines',AA:'G',col:'#F09147'},
				{category:'Prolines',AA:'P',col:'#C0BF00'},
				{category:'Aromatics',AA:'H / Y',col:'#17A4A4'}
			],
			text_to_copy: String
		}
	},
	mounted(){
	},
	methods:{
		copy_data_to_clipboard(txt){
			    var input = document.createElement("textarea");
			    document.body.appendChild(input);
			    input.value = txt;
			    input.select();
			    document.execCommand("copy");
			    document.body.removeChild(input);
			try {
            	var successful = document.execCommand('copy');
            	// var msg = successful ? 'successful' : 'unsuccessful';
            	// alert('Testing code was copied ' + msg);
        	} catch (err) {
            	alert('Oops, unable to copy');
        	}

		}
	}
	
}
</script>

<style>
	.hidden_header {
		display: none;
	}

	#table-contents ul ul a {
	  font-size: 16px !important;
	  padding-left: 0px !important;
	  background: #E9ECEF;
	  
	}
	#table-contents ul ul a:hover {
	  	text-decoration: underline;
	  }

	li.doc {
	  font-size: 20px !important;
	  li {
	    font-size: 16px !important;
	  }
	}

	hr {
		background-color: black;
	}

</style>
